import { useRolodexEdit } from 'features/fde'
import { useKestrel } from 'features/kestrelViewer'
import { FC, useState } from 'react'
import styled from 'styled-components'
import { RolodexEntryPatchJson, RolodexEntryType } from 'types'

import { SectionTitle } from './KestrelViewerMarkupSection'

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 12px;
  padding-bottom: 0.5rem;
`

const SectionTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`

const FieldsRow = styled.div`
  display: flex;
  gap: 0.5rem;
`

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
`

const Label = styled.label`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.forecast.defaultText};
  font-weight: 500;
`

const Input = styled.input`
  padding: 0.5rem;
  background: ${({ theme }) => theme.forecast.background3};
  border: 1px solid ${({ theme }) => theme.forecast.background4};
  border-radius: 4px;
  color: ${({ theme }) => theme.forecast.activeText};
  font-size: 0.875rem;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.forecast.primary};
  }

  &::placeholder {
    color: ${({ theme }) => theme.forecast.secondaryText};
  }
`

export const AerospaceMetadataSection: FC = () => {
  const { kestrelModel } = useKestrel()
  const { rolodexPatch } = useRolodexEdit()

  const [missionDesignSeries, setMissionDesignSeries] = useState(
    kestrelModel?.data.aerospaceMetadata?.missionDesignSeries ?? ''
  )
  const [serialNumber, setSerialNumber] = useState(
    kestrelModel?.data.aerospaceMetadata?.serialNumber ?? ''
  )

  const handleBlur = (
    field: 'missionDesignSeries' | 'serialNumber',
    value: string
  ) => {
    if (!kestrelModel?.id) return

    const patchData: RolodexEntryPatchJson = {
      id: kestrelModel.id,
      type: RolodexEntryType.KESTREL_MODEL,
      data: {
        aerospaceMetadata: {
          ...kestrelModel.data.aerospaceMetadata,
          [field]: value,
        },
      },
    }

    rolodexPatch([patchData]).catch((error) => {
      console.error('Failed to update aerospace metadata:', error)
    })
  }

  return (
    <Section>
      <SectionTitleRow>
        <SectionTitle>Aircraft Information</SectionTitle>
      </SectionTitleRow>
      <FieldsRow>
        <InputGroup>
          <Label htmlFor='mds-input'>MDS</Label>
          <Input
            id='mds-input'
            type='text'
            placeholder='e.g., C-130J'
            value={missionDesignSeries}
            onChange={(e) => setMissionDesignSeries(e.target.value)}
            onBlur={(e) => handleBlur('missionDesignSeries', e.target.value)}
          />
        </InputGroup>
        <InputGroup>
          <Label htmlFor='serial-number-input'>Serial Number</Label>
          <Input
            id='serial-number-input'
            type='text'
            placeholder='e.g., 12-3456'
            value={serialNumber}
            onChange={(e) => setSerialNumber(e.target.value)}
            onBlur={(e) => handleBlur('serialNumber', e.target.value)}
          />
        </InputGroup>
      </FieldsRow>
    </Section>
  )
}
